import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);

    // Log timeout errors specifically
    if (error.message?.includes('timeout') || error.message?.includes('Timeout')) {
      console.error('Timeout error detected:', {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
      });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EEE1E8] to-[#DCCFD7]">
          <div className="max-w-md w-full p-8 bg-white rounded-lg shadow-lg text-center">
            <div className="mb-6">
              <div className="flex items-center justify-center w-20 h-20 mx-auto bg-gradient-to-br from-[#AD0C62] to-[#621C43] rounded-full mb-6">
                <svg
                  className="w-10 h-10 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h1 className="text-5xl font-bold text-[#AD0C62] mb-4">Oops!</h1>
              <h2 className="text-2xl font-bold text-[#621C43] mb-2">Something went wrong</h2>
              <p className="text-gray-600 text-lg">
                {this.state.error?.message?.includes('timeout')
                  ? 'Request timeout. Please check your connection and try again.'
                  : 'An unexpected error occurred. Please refresh the page.'}
              </p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => window.location.reload()}
                className="flex-1 py-3 px-4 bg-[#EA600E] text-white rounded-md hover:bg-orange-600 transition font-medium"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
